import type {
    ShadowComparison,
    ShadowComparisonStatus,
    ShadowDeliveryCounts,
    ShadowMessage,
    ShadowSource,
    ShadowTallyCounts,
    ShadowTallySummary,
} from "./types";

import { GaiaWebSocketEvent } from "../types";
import { extractSessionId } from "./matchKey";

// 5 min, not 1: every connected client flushes on this cadence, so at ~2,300 experts a 60s window
// alone would be ~1M summary analytics a day.
export const DEFAULT_FLUSH_INTERVAL_MS = 300_000;

export interface ShadowTallyOptions {
    /** Receives each non-empty window summary (interval flush and final flush on dispose). */
    onSummary: (summary: ShadowTallySummary) => void;
    /** Window length in ms. Defaults to 5 min. */
    flushIntervalMs?: number;
    /** Clock, injectable for tests. */
    now?: () => number;
}

export const TALLY_COUNT_KEY: Readonly<Record<ShadowComparisonStatus, keyof ShadowTallyCounts>> = {
    equal: "equal",
    different: "different",
    "gaia-only": "gaiaOnly",
    "simplr-only": "simplrOnly",
};

/**
 * Counts shadow comparisons per `eventName` x `status`, plus per-side deliveries of the events that
 * are never compared, and periodically flushes a low-volume, PII-safe summary (event names and counts
 * only). This is what makes a match rate computable in EDP: per-message analytics fire only for
 * mismatches, so `equal` results would otherwise be invisible.
 * Empty windows are skipped. After `dispose()` the final window is flushed and nothing more ever fires.
 */
export class ShadowTally {
    private counts = new Map<string, ShadowTallyCounts>();
    private deliveries = new Map<string, ShadowDeliveryCounts>();
    private windowStartedAt: number;
    private sessionId?: string;
    private disposed = false;
    private timer: ReturnType<typeof setInterval> | undefined;
    private readonly now: () => number;
    private readonly options: ShadowTallyOptions;

    constructor(options: ShadowTallyOptions) {
        this.options = options;
        this.now = options.now ?? Date.now;
        this.windowStartedAt = this.now();
        this.timer = setInterval(() => this.flush(), options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS);
    }

    record(comparison: Pick<ShadowComparison, "eventName" | "status" | "sessionId">): void {
        if (this.disposed) return;
        let entry = this.counts.get(comparison.eventName);
        if (!entry) {
            entry = { equal: 0, different: 0, gaiaOnly: 0, simplrOnly: 0 };
            this.counts.set(comparison.eventName, entry);
        }
        entry[TALLY_COUNT_KEY[comparison.status]] += 1;
        if (comparison.sessionId) this.sessionId = comparison.sessionId;
    }

    /** Counts a message that is never compared (see `isShadowComparable`); transcripts also count snippets. */
    recordDelivery(source: ShadowSource, message: ShadowMessage): void {
        if (this.disposed) return;
        let entry = this.deliveries.get(message.name);
        if (!entry) {
            entry = { gaia: 0, simplr: 0 };
            this.deliveries.set(message.name, entry);
        }
        entry[source] += 1;
        if (message.name === GaiaWebSocketEvent.TranscriptSnippets) {
            const snippetKey = source === "gaia" ? "gaiaSnippets" : "simplrSnippets";
            entry[snippetKey] = (entry[snippetKey] ?? 0) + countSnippets(message);
        }
        const sessionId = extractSessionId(message);
        if (sessionId) this.sessionId = sessionId;
    }

    /** Emits the current window if it has any counts, then starts a new window. */
    flush(): void {
        if (this.disposed) return;
        this.emit();
    }

    /** Flushes the tail of the session once, stops the timer, and ignores everything afterwards. */
    dispose(): void {
        if (this.disposed) return;
        // Stop first so a throwing onSummary cannot leave the interval running.
        this.disposed = true;
        if (this.timer !== undefined) clearInterval(this.timer);
        this.timer = undefined;
        this.emit();
    }

    private emit(): void {
        const endedAt = this.now();
        if (this.counts.size === 0 && this.deliveries.size === 0) {
            this.windowStartedAt = endedAt;
            return;
        }
        const summary: ShadowTallySummary = {
            windowStartedAt: new Date(this.windowStartedAt).toISOString(),
            windowEndedAt: new Date(endedAt).toISOString(),
            durationMs: endedAt - this.windowStartedAt,
            ...(this.sessionId ? { sessionId: this.sessionId } : {}),
            counts: Object.fromEntries(this.counts),
            ...(this.deliveries.size > 0 ? { deliveries: Object.fromEntries(this.deliveries) } : {}),
        };
        this.counts = new Map();
        this.deliveries = new Map();
        this.sessionId = undefined;
        this.windowStartedAt = endedAt;
        this.options.onSummary(summary);
    }
}

function countSnippets(message: ShadowMessage): number {
    const transcripts = (message.body as { transcripts?: unknown } | null | undefined)?.transcripts;
    return Array.isArray(transcripts) ? transcripts.length : 0;
}
